import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from './components/crm/Sidebar';
import { Dashboard } from './components/crm/Dashboard';
import { Contacts } from './components/crm/Contacts';
import { Tasks } from './components/crm/Tasks';
import { Pipeline } from './components/crm/Pipeline';
import { Invoices } from './components/crm/Invoices';
import { Financials } from './components/crm/Financials';
import { Vendors } from './components/crm/Vendors';
import { Emails } from './components/crm/Emails';
import { Store } from './components/crm/Store';
import { Leads } from './components/crm/Leads';
import { AdminSettings } from './components/crm/AdminSettings';
import AdminLogin from './components/crm/AdminLogin';
import Storefront from './components/crm/Storefront';
import ContactPage from './components/crm/ContactPage';
import PrivacyPage from './components/crm/PrivacyPage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function CRMDashboard() {
  const [admin, setAdmin] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeNav, setActiveNav] = useState("dashboard");
  const [stats, setStats] = useState({
    total_contacts: 0, total_deals: 0, total_pipeline_value: 0,
    tasks_due_today: 0, open_tasks: 0, deals_won: 0, deals_by_stage: {},
    total_invoices: 0, overdue_invoices: 0, overdue_amount: 0, total_revenue: 0, pending_payments: 0
  });
  const [contacts, setContacts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [emails, setEmails] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkAdminAuth = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { setAdmin(null); setAuthChecked(true); return; }
    try {
      const res = await fetch(`${API}/admin-auth/me`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setAdmin(await res.json());
      } else {
        localStorage.removeItem("admin_token");
        setAdmin(null);
      }
    } catch {
      setAdmin(null);
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => { checkAdminAuth(); }, [checkAdminAuth]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, contactsRes, tasksRes, dealsRes, emailsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/tasks`),
        axios.get(`${API}/deals`),
        axios.get(`${API}/emails`),
        axios.get(`${API}/invoices`)
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setTasks(tasksRes.data);
      setDeals(dealsRes.data);
      setEmails(emailsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin) fetchData();
  }, [admin, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setAdmin(null);
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-royal-blue-200 border-t-royal-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!admin) {
    return <AdminLogin onLogin={setAdmin} />;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-royal-blue-200 border-t-royal-blue-700 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading CRM data...</p>
          </div>
        </div>
      );
    }

    switch (activeNav) {
      case "dashboard":
        return <Dashboard stats={stats} contacts={contacts} tasks={tasks} deals={deals} invoices={invoices} />;
      case "leads":
        return <Leads onRefresh={fetchData} />;
      case "contacts":
        return <Contacts contacts={contacts} onRefresh={fetchData} />;
      case "tasks":
        return <Tasks tasks={tasks} contacts={contacts} deals={deals} onRefresh={fetchData} />;
      case "pipeline":
        return <Pipeline deals={deals} contacts={contacts} onRefresh={fetchData} />;
      case "invoices":
        return <Invoices invoices={invoices} contacts={contacts} onRefresh={fetchData} />;
      case "financials":
        return <Financials invoices={invoices} onRefresh={fetchData} />;
      case "vendors":
        return <Vendors contacts={contacts} onRefresh={fetchData} />;
      case "store":
        return <Store contacts={contacts} onRefresh={fetchData} />;
      case "emails":
        return <Emails emails={emails} contacts={contacts} onRefresh={fetchData} />;
      case "settings":
        return <AdminSettings admin={admin} />;
      default:
        return <Dashboard stats={stats} contacts={contacts} tasks={tasks} deals={deals} invoices={invoices} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeNav={activeNav} setActiveNav={setActiveNav} admin={admin} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        {renderContent()}
      </main>
    </div>
  );
}

function App() {
  const hostname = window.location.hostname;
  const isStoreDomain = hostname === 'store.oplogman.com';
  const isMainSite = hostname === 'oplogman.com' || hostname === 'www.oplogman.com';

  return (
    <BrowserRouter>
      <Routes>
        {isStoreDomain ? (
          <>
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/*" element={<Storefront />} />
          </>
        ) : isMainSite ? (
          <>
            <Route path="/store" element={<Storefront />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/*" element={<ContactPage />} />
          </>
        ) : (
          <>
            <Route path="/store" element={<Storefront />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/*" element={<CRMDashboard />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
